import { useState, useEffect, useRef } from 'react';
import { X, FileText, Download, Send, Clock, Package, Image, Pen, AlertCircle, CheckCircle, Loader2, Plus, Trash2, Edit2, Save, Camera, Search, WifiOff, CloudOff } from '../../icons/wp-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { getArbeitsnachweisData, generateArbeitsnachweis, sendArbeitsnachweis } from '../../api/arbeitsnachweisApi';
import { unterschriftApi } from '../../api/unterschriftApi';
import apiClient from '../../api/apiClient';
import SignaturePad from '../Unterschrift/SignaturePad';

// Animation-Varianten (wie in Common/Modal.jsx)
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    scale: 1
  },
  exit: {
    opacity: 0,
    scale: 0.95
  }
};

function ArbeitsnachweisModal({ anfrageId, kunde, onClose, onGenerated }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [generatedResult, setGeneratedResult] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);

  // Animation: Sichtbarkeit für Exit-Animation
  const [isVisible, setIsVisible] = useState(true);

  // Online/Offline Status
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Online/Offline Event Listener
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Schließen mit Animation
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 150); // Gleiche Dauer wie Exit-Animation
  };

  // Daten-States
  const [anfrage, setAnfrage] = useState(null);
  const [arbeitszeiten, setArbeitszeiten] = useState([]);
  const [materialien, setMaterialien] = useState([]);
  const [fotos, setFotos] = useState([]);
  const [unterschriften, setUnterschriften] = useState([]);
  const [bemerkungen, setBemerkungen] = useState('');

  // Unterschrift
  const [mitUnterschrift, setMitUnterschrift] = useState(false);
  const [selectedUnterschriftId, setSelectedUnterschriftId] = useState(null);

  // Option: Neue Einträge speichern
  const [saveToSystem, setSaveToSystem] = useState(false);

  // Bearbeitungs-States
  const [editingArbeitszeitId, setEditingArbeitszeitId] = useState(null);
  const [editingMaterialId, setEditingMaterialId] = useState(null);
  const [showAddArbeitszeit, setShowAddArbeitszeit] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);

  // Neue Arbeitszeit Formular
  const [newArbeitszeit, setNewArbeitszeit] = useState({
    datum: new Date().toISOString().split('T')[0],
    taetigkeit: '',
    vonZeit: '08:00',
    bisZeit: '12:00',
    mitarbeiter: ''
  });

  // Neues Material Formular
  const [newMaterial, setNewMaterial] = useState({
    bezeichnung: '',
    menge: 1,
    einheit: 'Stück',
    katalogPositionId: null
  });

  // Material-Eingabemodus: 'katalog' oder 'custom'
  const [materialInputMode, setMaterialInputMode] = useState('katalog');

  // Material-Autocomplete
  const [materialSuggestions, setMaterialSuggestions] = useState([]);
  const [showMaterialSuggestions, setShowMaterialSuggestions] = useState(false);
  const [searchingMaterial, setSearchingMaterial] = useState(false);
  const materialSearchTimeout = useRef(null);
  const materialInputRef = useRef(null);

  // Material-Suche im Katalog
  const searchMaterialKatalog = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setMaterialSuggestions([]);
      return;
    }

    try {
      setSearchingMaterial(true);
      const response = await apiClient.get(`/katalog/autocomplete?search=${encodeURIComponent(searchTerm)}`);
      setMaterialSuggestions(response.data || []);
      setShowMaterialSuggestions(true);
    } catch (err) {
      console.error('Fehler bei Material-Suche:', err);
      setMaterialSuggestions([]);
    } finally {
      setSearchingMaterial(false);
    }
  };

  // Debounced Material-Suche
  const handleMaterialSearchChange = (value) => {
    setNewMaterial({ ...newMaterial, bezeichnung: value, katalogPositionId: null });

    if (materialSearchTimeout.current) {
      clearTimeout(materialSearchTimeout.current);
    }

    materialSearchTimeout.current = setTimeout(() => {
      searchMaterialKatalog(value);
    }, 300);
  };

  // Material aus Katalog auswählen
  const selectMaterialFromKatalog = (katalogItem) => {
    setNewMaterial({
      bezeichnung: katalogItem.bezeichnung,
      menge: 1,
      einheit: katalogItem.einheit || 'Stück',
      katalogPositionId: katalogItem.id
    });
    setShowMaterialSuggestions(false);
    setMaterialSuggestions([]);
  };

  // Daten laden
  useEffect(() => {
    loadData();
  }, [anfrageId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getArbeitsnachweisData(anfrageId);
      const data = result.data;

      setAnfrage(data.anfrage);
      setUnterschriften(data.unterschriften || []);
      setFotos(data.fotos || []);

      // Zeiterfassungen in editierbare Liste umwandeln
      const zeiten = (data.zeiterfassungen || []).map((ze, idx) => ({
        id: ze.id || `existing-${idx}`,
        datum: ze.datum ? new Date(ze.datum).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        taetigkeit: ze.taetigkeit || '',
        vonZeit: ze.startzeit || '08:00',
        bisZeit: ze.endzeit || '17:00',
        minuten: ze.gesamtminuten || 0,
        mitarbeiter: ze.mitarbeiter || '',
        isNew: false
      }));
      setArbeitszeiten(zeiten);

      // Materialien in editierbare Liste umwandeln
      const mats = (data.materialien || []).map((mat, idx) => ({
        id: mat.id || `existing-${idx}`,
        bezeichnung: mat.bezeichnung || mat.name || '',
        menge: mat.menge || 1,
        einheit: mat.einheit || 'Stück',
        isNew: false
      }));
      setMaterialien(mats);

      // Wenn bereits eine Abnahme-Unterschrift vorhanden, diese vorauswählen
      const abnahme = data.unterschriften?.find(u => u.typ === 'Abnahme' && u.hatSignatur);
      if (abnahme) {
        setSelectedUnterschriftId(abnahme.id);
        setMitUnterschrift(true);
      }
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError(err.response?.data?.error || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Zeit berechnen (von-bis zu Minuten)
  const berechneMinuten = (von, bis) => {
    if (!von || !bis) return 0;
    const [vonH, vonM] = von.split(':').map(Number);
    const [bisH, bisM] = bis.split(':').map(Number);
    return (bisH * 60 + bisM) - (vonH * 60 + vonM);
  };

  // Minuten zu Stunden-String
  const formatStunden = (minuten) => {
    if (!minuten || minuten <= 0) return '0:00 Std.';
    const stunden = Math.floor(minuten / 60);
    const restMinuten = minuten % 60;
    return `${stunden}:${restMinuten.toString().padStart(2, '0')} Std.`;
  };

  // Datum formatieren
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Gesamtstunden berechnen
  const gesamtMinuten = arbeitszeiten.reduce((sum, az) => {
    const min = az.minuten || berechneMinuten(az.vonZeit, az.bisZeit);
    return sum + min;
  }, 0);

  // Arbeitszeit hinzufügen
  const handleAddArbeitszeit = () => {
    const minuten = berechneMinuten(newArbeitszeit.vonZeit, newArbeitszeit.bisZeit);
    const neueArbeitszeit = {
      id: `new-${Date.now()}`,
      ...newArbeitszeit,
      minuten,
      isNew: true
    };
    setArbeitszeiten([...arbeitszeiten, neueArbeitszeit]);
    setNewArbeitszeit({
      datum: new Date().toISOString().split('T')[0],
      taetigkeit: '',
      vonZeit: '08:00',
      bisZeit: '12:00',
      mitarbeiter: ''
    });
    setShowAddArbeitszeit(false);
  };

  // Arbeitszeit löschen
  const handleDeleteArbeitszeit = (id) => {
    setArbeitszeiten(arbeitszeiten.filter(az => az.id !== id));
  };

  // Arbeitszeit bearbeiten speichern
  const handleSaveArbeitszeit = (id, updatedData) => {
    const minuten = berechneMinuten(updatedData.vonZeit, updatedData.bisZeit);
    setArbeitszeiten(arbeitszeiten.map(az =>
      az.id === id ? { ...az, ...updatedData, minuten } : az
    ));
    setEditingArbeitszeitId(null);
  };

  // Material hinzufügen
  const handleAddMaterial = () => {
    const neuesMaterial = {
      id: `new-${Date.now()}`,
      bezeichnung: newMaterial.bezeichnung,
      menge: newMaterial.menge,
      einheit: newMaterial.einheit,
      katalogPositionId: newMaterial.katalogPositionId,
      isNew: true
    };
    setMaterialien([...materialien, neuesMaterial]);
    setNewMaterial({
      bezeichnung: '',
      menge: 1,
      einheit: 'Stück',
      katalogPositionId: null
    });
    setMaterialSuggestions([]);
    setShowMaterialSuggestions(false);
    setShowAddMaterial(false);
  };

  // Material löschen
  const handleDeleteMaterial = (id) => {
    setMaterialien(materialien.filter(m => m.id !== id));
  };

  // Material bearbeiten speichern
  const handleSaveMaterial = (id, updatedData) => {
    setMaterialien(materialien.map(m =>
      m.id === id ? { ...m, ...updatedData } : m
    ));
    setEditingMaterialId(null);
  };

  // Unterschrift speichern
  const handleSignatureSave = async (signatureData) => {
    try {
      const result = await unterschriftApi.create({
        anfrageId,
        ...signatureData,
        typ: 'Abnahme'
      });

      // Aktuelle manuelle Eingaben sichern bevor loadData() aufgerufen wird
      const savedArbeitszeiten = [...arbeitszeiten];
      const savedMaterialien = [...materialien];
      const savedBemerkungen = bemerkungen;

      // Nur Unterschriften neu laden
      const response = await getArbeitsnachweisData(anfrageId);
      const data = response.data;

      // Unterschriften aktualisieren
      const unterschriftenFormatiert = (data.unterschriften || []).map(u => ({
        id: u.id,
        typ: u.typ,
        signiertVon: u.signiertVon || 'Unbekannt',
        datum: u.datum,
        hatSignatur: !!u.hatSignatur
      }));
      setUnterschriften(unterschriftenFormatiert);

      // Manuelle Eingaben wiederherstellen
      setArbeitszeiten(savedArbeitszeiten);
      setMaterialien(savedMaterialien);
      setBemerkungen(savedBemerkungen);

      setShowSignaturePad(false);
      setSelectedUnterschriftId(result.id);
      setMitUnterschrift(true);
    } catch (err) {
      console.error('Fehler beim Speichern der Unterschrift:', err);
      throw err;
    }
  };

  // PDF generieren
  const handleGenerate = async () => {
    // Warnung wenn keine Arbeitszeiten vorhanden
    if (arbeitszeiten.length === 0) {
      const confirmed = window.confirm(
        'Es sind keine Arbeitszeiten eingetragen.\n\n' +
        'Möchten Sie den Arbeitsnachweis trotzdem ohne Zeiterfassung erstellen?\n\n' +
        'Tipp: Klicken Sie auf "Hinzufügen" bei ARBEITSZEITEN um Einträge manuell zu erfassen.'
      );
      if (!confirmed) return;
    }

    try {
      setGenerating(true);
      setError(null);

      const options = {
        mitUnterschrift,
        unterschriftId: selectedUnterschriftId,
        bemerkungen: bemerkungen.trim(),
        saveToSystem,
        // Custom Daten aus dem Modal
        customArbeitszeiten: arbeitszeiten.map(az => ({
          datum: az.datum,
          taetigkeit: az.taetigkeit,
          vonZeit: az.vonZeit,
          bisZeit: az.bisZeit,
          minuten: az.minuten || berechneMinuten(az.vonZeit, az.bisZeit),
          mitarbeiter: az.mitarbeiter,
          isNew: az.isNew
        })),
        customMaterialien: materialien.map(m => ({
          bezeichnung: m.bezeichnung,
          menge: m.menge,
          einheit: m.einheit,
          isNew: m.isNew
        }))
      };

      // Offline-Daten für lokale Speicherung (falls offline)
      const offlineData = !isOnline ? {
        kunde,
        anfrage,
        arbeitszeiten,
        materialien,
        fotos
      } : null;

      const result = await generateArbeitsnachweis(anfrageId, options, offlineData);

      setGeneratedResult(result.data);

      if (onGenerated) {
        onGenerated(result.data);
      }
    } catch (err) {
      console.error('Fehler beim Generieren:', err);
      setError(err.response?.data?.error || 'Fehler beim Generieren des PDFs');
    } finally {
      setGenerating(false);
    }
  };

  // PDF senden
  const handleSend = async () => {
    if (!generatedResult?.dokumentId) return;

    try {
      setSending(true);
      setError(null);

      await sendArbeitsnachweis(anfrageId, {
        dokumentId: generatedResult.dokumentId,
        empfaengerEmail: kunde?.email
      });

      alert(`Arbeitsnachweis wurde an ${kunde?.email} gesendet`);
    } catch (err) {
      console.error('Fehler beim Senden:', err);
      setError(err.response?.data?.error || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  // PDF herunterladen
  const handleDownload = () => {
    if (!generatedResult?.dateipfad) return;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    window.open(`${apiUrl}${generatedResult.dateipfad}`, '_blank');
  };

  // SignaturePad anzeigen
  if (showSignaturePad) {
    return (
      <SignaturePad
        onSave={handleSignatureSave}
        onClose={() => setShowSignaturePad(false)}
      />
    );
  }

  return (
    <AnimatePresence>
      {isVisible && (
      <div className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden">
        {/* Animated Backdrop */}
        <motion.div
          className="fixed inset-0 bg-black/50"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={{ duration: 0.15, ease: "easeOut" }}
          onClick={handleClose}
        />

        {/* Modal Container */}
        <div className="flex min-h-full items-start justify-center p-2 pb-24 pt-2 md:p-4 md:items-center" style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}>
          {/* Animated Modal Content */}
          <motion.div
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] md:max-h-[90vh] overflow-hidden flex flex-col mt-0 md:mt-0"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{
              duration: 0.15,
              ease: "easeOut",
              exit: { duration: 0.1, ease: "easeIn" }
            }}
            onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <FileText className="w-5 h-5 md:w-6 md:h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900">Arbeitsnachweis erstellen</h2>
              <p className="text-xs md:text-sm text-gray-500">{anfrage?.betreff || 'Anfrage'}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Offline-Hinweis */}
        {!isOnline && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200">
            <WifiOff className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-700 font-medium">Offline-Modus</span>
            <span className="text-xs text-amber-600">— Arbeitsnachweis wird lokal gespeichert</span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : error && !anfrage ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 text-red-700 rounded-xl">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          ) : (
            <>
              {/* Erfolgs-Nachricht wenn generiert */}
              {generatedResult && (
                <div className={`flex items-center gap-3 p-4 rounded-xl ${generatedResult.offline ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
                  {generatedResult.offline ? (
                    <CloudOff className="w-5 h-5 flex-shrink-0" />
                  ) : (
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  )}
                  <div>
                    {generatedResult.offline ? (
                      <>
                        <p className="font-medium">Arbeitsnachweis für Offline-Sync gespeichert</p>
                        <p className="text-sm">Das PDF wird automatisch erstellt, sobald Sie wieder online sind.</p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium">Arbeitsnachweis {generatedResult.nummer} wurde erstellt!</p>
                        <p className="text-sm">Das Dokument wurde beim Kunden abgelegt.</p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Fehler-Nachricht */}
              {error && (
                <div className="flex items-center gap-3 p-4 bg-red-50 text-red-700 rounded-xl">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}

              {/* Kunde Info */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-sm text-gray-500">Kunde</p>
                <p className="font-semibold text-gray-900">{kunde?.name || kunde?.firma || 'Unbekannt'}</p>
                {kunde?.adresse && <p className="text-sm text-gray-600">{kunde.adresse}</p>}
              </div>

              {/* ARBEITSZEITEN */}
              {!generatedResult && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      ARBEITSZEITEN
                    </h3>
                    <button
                      onClick={() => setShowAddArbeitszeit(true)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                    >
                      <Plus className="w-4 h-4" />
                      Hinzufügen
                    </button>
                  </div>

                  {/* Neue Arbeitszeit Formular */}
                  {showAddArbeitszeit && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Datum</label>
                          <input
                            type="date"
                            value={newArbeitszeit.datum}
                            onChange={(e) => setNewArbeitszeit({...newArbeitszeit, datum: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div className="col-span-2 md:col-span-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Tätigkeit</label>
                          <input
                            type="text"
                            value={newArbeitszeit.taetigkeit}
                            onChange={(e) => setNewArbeitszeit({...newArbeitszeit, taetigkeit: e.target.value})}
                            placeholder="z.B. Fenster montiert"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Von</label>
                          <input
                            type="time"
                            value={newArbeitszeit.vonZeit}
                            onChange={(e) => setNewArbeitszeit({...newArbeitszeit, vonZeit: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Bis</label>
                          <input
                            type="time"
                            value={newArbeitszeit.bisZeit}
                            onChange={(e) => setNewArbeitszeit({...newArbeitszeit, bisZeit: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setShowAddArbeitszeit(false)}
                          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
                        >
                          Abbrechen
                        </button>
                        <button
                          onClick={handleAddArbeitszeit}
                          disabled={!newArbeitszeit.taetigkeit}
                          className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50"
                        >
                          Hinzufügen
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Arbeitszeiten Liste */}
                  {arbeitszeiten.length > 0 ? (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="divide-y divide-gray-100">
                        {arbeitszeiten.map((az) => (
                          <div key={az.id} className="p-3 hover:bg-gray-50">
                            {editingArbeitszeitId === az.id ? (
                              <ArbeitszeitEditRow
                                arbeitszeit={az}
                                onSave={(data) => handleSaveArbeitszeit(az.id, data)}
                                onCancel={() => setEditingArbeitszeitId(null)}
                              />
                            ) : (
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900">{formatDate(az.datum)}</span>
                                    <span className="text-sm text-gray-600 truncate">{az.taetigkeit}</span>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {az.vonZeit} - {az.bisZeit} • {formatStunden(az.minuten || berechneMinuten(az.vonZeit, az.bisZeit))}
                                    {az.mitarbeiter && ` • ${az.mitarbeiter}`}
                                    {az.isNew && <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Neu</span>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                  <button
                                    onClick={() => setEditingArbeitszeitId(az.id)}
                                    className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded transition"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteArbeitszeit(az.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                        <p className="text-sm font-semibold text-gray-700">
                          Gesamt: {formatStunden(gesamtMinuten)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-xl">
                      <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Keine Arbeitszeiten vorhanden</p>
                      <p className="text-xs">Klicken Sie auf "Hinzufügen" um Arbeitszeiten zu erfassen</p>
                    </div>
                  )}
                </div>
              )}

              {/* MATERIAL */}
              {!generatedResult && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      MATERIAL
                    </h3>
                    <button
                      onClick={() => setShowAddMaterial(true)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                    >
                      <Plus className="w-4 h-4" />
                      Hinzufügen
                    </button>
                  </div>

                  {/* Neues Material Formular mit Katalog-Autocomplete */}
                  {showAddMaterial && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      {/* Modus-Umschalter */}
                      <div className="flex gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            setMaterialInputMode('katalog');
                            setNewMaterial({ bezeichnung: '', menge: 1, einheit: 'Stück', katalogPositionId: null });
                          }}
                          className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition ${
                            materialInputMode === 'katalog'
                              ? 'bg-primary text-white'
                              : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <Search className="w-3 h-3 inline mr-1" />
                          Aus Katalog
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMaterialInputMode('custom');
                            setNewMaterial({ bezeichnung: '', menge: 1, einheit: 'Stück', katalogPositionId: null });
                            setMaterialSuggestions([]);
                            setShowMaterialSuggestions(false);
                          }}
                          className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition ${
                            materialInputMode === 'custom'
                              ? 'bg-primary text-white'
                              : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <Edit2 className="w-3 h-3 inline mr-1" />
                          Freie Eingabe
                        </button>
                      </div>

                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="col-span-3 md:col-span-1 relative">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Bezeichnung
                            {newMaterial.katalogPositionId && (
                              <span className="ml-2 text-green-600">(aus Katalog)</span>
                            )}
                          </label>

                          {/* Katalog-Suche Modus */}
                          {materialInputMode === 'katalog' ? (
                            <div className="relative">
                              <input
                                ref={materialInputRef}
                                type="text"
                                value={newMaterial.bezeichnung}
                                onChange={(e) => handleMaterialSearchChange(e.target.value)}
                                onFocus={() => newMaterial.bezeichnung.length >= 2 && setShowMaterialSuggestions(true)}
                                onBlur={() => setTimeout(() => setShowMaterialSuggestions(false), 200)}
                                placeholder="Suche im Katalog..."
                                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm"
                              />
                              {searchingMaterial && (
                                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                              )}
                              {!searchingMaterial && (
                                <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              )}

                              {/* Autocomplete Dropdown */}
                              {showMaterialSuggestions && materialSuggestions.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                  {materialSuggestions.map((item) => (
                                    <button
                                      key={item.id}
                                      type="button"
                                      onClick={() => selectMaterialFromKatalog(item)}
                                      className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                                    >
                                      <div className="font-medium text-sm text-gray-900">{item.bezeichnung}</div>
                                      <div className="text-xs text-gray-500 flex gap-2">
                                        {item.artikelnummer && <span>Art.Nr: {item.artikelnummer}</span>}
                                        <span>{item.einheit}</span>
                                        {item.kategorie && <span>• {item.kategorie}</span>}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}

                              {/* Hinweis wenn nichts gefunden */}
                              {showMaterialSuggestions && materialSuggestions.length === 0 && newMaterial.bezeichnung.length >= 2 && !searchingMaterial && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                                  <p className="text-xs text-gray-500">Nichts gefunden. Nutze "Freie Eingabe" für eigene Einträge.</p>
                                </div>
                              )}
                            </div>
                          ) : (
                            /* Freie Eingabe Modus */
                            <input
                              type="text"
                              value={newMaterial.bezeichnung}
                              onChange={(e) => setNewMaterial({ ...newMaterial, bezeichnung: e.target.value })}
                              placeholder="z.B. Silikonkartusche weiß"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Menge</label>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={newMaterial.menge}
                            onChange={(e) => setNewMaterial({...newMaterial, menge: parseFloat(e.target.value) || 1})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Einheit</label>
                          <select
                            value={newMaterial.einheit}
                            onChange={(e) => setNewMaterial({...newMaterial, einheit: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="Stück">Stück</option>
                            <option value="m">m</option>
                            <option value="m²">m²</option>
                            <option value="lfm">lfm</option>
                            <option value="kg">kg</option>
                            <option value="Liter">Liter</option>
                            <option value="Paket">Paket</option>
                            <option value="Set">Set</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setShowAddMaterial(false);
                            setMaterialSuggestions([]);
                            setShowMaterialSuggestions(false);
                            setMaterialInputMode('katalog');
                            setNewMaterial({ bezeichnung: '', menge: 1, einheit: 'Stück', katalogPositionId: null });
                          }}
                          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
                        >
                          Abbrechen
                        </button>
                        <button
                          onClick={() => {
                            handleAddMaterial();
                            setMaterialInputMode('katalog');
                          }}
                          disabled={!newMaterial.bezeichnung}
                          className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50"
                        >
                          Hinzufügen
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Material Liste */}
                  {materialien.length > 0 ? (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="divide-y divide-gray-100">
                        {materialien.map((mat) => (
                          <div key={mat.id} className="p-3 hover:bg-gray-50">
                            {editingMaterialId === mat.id ? (
                              <MaterialEditRow
                                material={mat}
                                onSave={(data) => handleSaveMaterial(mat.id, data)}
                                onCancel={() => setEditingMaterialId(null)}
                              />
                            ) : (
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm text-gray-900">
                                    {mat.menge} {mat.einheit} {mat.bezeichnung}
                                  </span>
                                  {mat.isNew && <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Neu</span>}
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                  <button
                                    onClick={() => setEditingMaterialId(mat.id)}
                                    className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded transition"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteMaterial(mat.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-xl">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Kein Material vorhanden</p>
                      <p className="text-xs">Klicken Sie auf "Hinzufügen" um Material zu erfassen</p>
                    </div>
                  )}
                </div>
              )}

              {/* FOTOS */}
              {fotos.length > 0 && !generatedResult && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                    <Image className="w-4 h-4" />
                    FOTOS ({fotos.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {fotos.slice(0, 6).map((foto, idx) => (
                      <div key={idx} className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={foto.url || foto.pfad}
                          alt={`Foto ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                    {fotos.length > 6 && (
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                        +{fotos.length - 6}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* BEMERKUNGEN */}
              {!generatedResult && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    BEMERKUNGEN
                  </label>
                  <textarea
                    value={bemerkungen}
                    onChange={(e) => setBemerkungen(e.target.value)}
                    placeholder="Zusätzliche Anmerkungen zum Arbeitsnachweis..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                  />
                </div>
              )}

              {/* UNTERSCHRIFT */}
              {!generatedResult && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                    <Pen className="w-4 h-4" />
                    UNTERSCHRIFT
                  </h3>

                  {unterschriften?.filter(u => u.hatSignatur).length > 0 ? (
                    <div className="space-y-2">
                      {unterschriften.filter(u => u.hatSignatur).map((u) => (
                        <label
                          key={u.id}
                          className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition ${
                            selectedUnterschriftId === u.id
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="unterschrift"
                            checked={selectedUnterschriftId === u.id}
                            onChange={() => {
                              setSelectedUnterschriftId(u.id);
                              setMitUnterschrift(true);
                            }}
                            className="w-4 h-4 text-primary"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 text-sm">{u.signiertVon}</p>
                            <p className="text-xs text-gray-500">
                              {u.typ} - {formatDate(u.datum)}
                            </p>
                          </div>
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        </label>
                      ))}

                      <label
                        className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition ${
                          !mitUnterschrift
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="unterschrift"
                          checked={!mitUnterschrift}
                          onChange={() => {
                            setMitUnterschrift(false);
                            setSelectedUnterschriftId(null);
                          }}
                          className="w-4 h-4 text-primary"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">Ohne Unterschrift</p>
                          <p className="text-xs text-gray-500">Arbeitsnachweis ohne Abnahme erstellen</p>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <p className="text-amber-800 text-sm">Keine Unterschrift vorhanden</p>
                      </div>
                      <button
                        onClick={() => setShowSignaturePad(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition text-sm font-medium"
                      >
                        <Pen className="w-4 h-4" />
                        Unterschreiben
                      </button>
                    </div>
                  )}

                  {unterschriften?.filter(u => u.hatSignatur).length > 0 && (
                    <button
                      onClick={() => setShowSignaturePad(true)}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-primary hover:text-primary transition text-sm"
                    >
                      <Pen className="w-4 h-4" />
                      Neue Unterschrift hinzufügen
                    </button>
                  )}
                </div>
              )}

              {/* Option: Im System speichern */}
              {!generatedResult && (arbeitszeiten.some(az => az.isNew) || materialien.some(m => m.isNew)) && (
                <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveToSystem}
                    onChange={(e) => setSaveToSystem(e.target.checked)}
                    className="w-4 h-4 text-primary rounded"
                  />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Neue Einträge auch im System speichern</p>
                    <p className="text-xs text-gray-500">Neue Arbeitszeiten und Materialien werden dauerhaft gespeichert</p>
                  </div>
                </label>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 md:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={handleClose}
            className="px-4 md:px-6 py-2.5 md:py-3 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition font-medium text-sm"
          >
            {generatedResult ? 'Schließen' : 'Abbrechen'}
          </button>

          {generatedResult ? (
            <>
              {/* Download und Senden nur wenn online erstellt (nicht bei Offline-Sync) */}
              {!generatedResult.offline && (
                <>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition font-medium text-sm"
                  >
                    <Download className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="hidden md:inline">Download</span>
                  </button>
                  {kunde?.email && (
                    <button
                      onClick={handleSend}
                      disabled={sending}
                      className="flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition font-medium disabled:opacity-50 text-sm"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                          <span className="hidden md:inline">Senden...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 md:w-5 md:h-5" />
                          <span className="hidden md:inline">Per E-Mail senden</span>
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
            </>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={generating || loading}
              className={`flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 text-white rounded-xl transition font-medium disabled:opacity-50 text-sm ${isOnline ? 'bg-primary hover:bg-primary-dark' : 'bg-amber-600 hover:bg-amber-700'}`}
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                  <span>{isOnline ? 'Generieren...' : 'Speichern...'}</span>
                </>
              ) : isOnline ? (
                <>
                  <FileText className="w-4 h-4 md:w-5 md:h-5" />
                  <span>PDF erstellen</span>
                </>
              ) : (
                <>
                  <CloudOff className="w-4 h-4 md:w-5 md:h-5" />
                  <span>Offline speichern</span>
                </>
              )}
            </button>
          )}
        </div>
          </motion.div>
        </div>
      </div>
      )}
    </AnimatePresence>
  );
}

// Inline Edit Row für Arbeitszeit
function ArbeitszeitEditRow({ arbeitszeit, onSave, onCancel }) {
  const [data, setData] = useState({
    datum: arbeitszeit.datum,
    taetigkeit: arbeitszeit.taetigkeit,
    vonZeit: arbeitszeit.vonZeit,
    bisZeit: arbeitszeit.bisZeit,
    mitarbeiter: arbeitszeit.mitarbeiter || ''
  });

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <input
          type="date"
          value={data.datum}
          onChange={(e) => setData({...data, datum: e.target.value})}
          className="px-2 py-1.5 border border-gray-300 rounded text-sm"
        />
        <input
          type="text"
          value={data.taetigkeit}
          onChange={(e) => setData({...data, taetigkeit: e.target.value})}
          placeholder="Tätigkeit"
          className="px-2 py-1.5 border border-gray-300 rounded text-sm col-span-2 md:col-span-1"
        />
        <input
          type="time"
          value={data.vonZeit}
          onChange={(e) => setData({...data, vonZeit: e.target.value})}
          className="px-2 py-1.5 border border-gray-300 rounded text-sm"
        />
        <input
          type="time"
          value={data.bisZeit}
          onChange={(e) => setData({...data, bisZeit: e.target.value})}
          className="px-2 py-1.5 border border-gray-300 rounded text-sm"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded">
          Abbrechen
        </button>
        <button onClick={() => onSave(data)} className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary-dark">
          Speichern
        </button>
      </div>
    </div>
  );
}

// Inline Edit Row für Material
function MaterialEditRow({ material, onSave, onCancel }) {
  const [data, setData] = useState({
    bezeichnung: material.bezeichnung,
    menge: material.menge,
    einheit: material.einheit
  });

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <input
          type="text"
          value={data.bezeichnung}
          onChange={(e) => setData({...data, bezeichnung: e.target.value})}
          placeholder="Bezeichnung"
          className="px-2 py-1.5 border border-gray-300 rounded text-sm col-span-3 md:col-span-1"
        />
        <input
          type="number"
          min="0.01"
          step="0.01"
          value={data.menge}
          onChange={(e) => setData({...data, menge: parseFloat(e.target.value) || 1})}
          className="px-2 py-1.5 border border-gray-300 rounded text-sm"
        />
        <select
          value={data.einheit}
          onChange={(e) => setData({...data, einheit: e.target.value})}
          className="px-2 py-1.5 border border-gray-300 rounded text-sm"
        >
          <option value="Stück">Stück</option>
          <option value="m">m</option>
          <option value="m²">m²</option>
          <option value="lfm">lfm</option>
          <option value="kg">kg</option>
          <option value="Liter">Liter</option>
          <option value="Paket">Paket</option>
          <option value="Set">Set</option>
        </select>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded">
          Abbrechen
        </button>
        <button onClick={() => onSave(data)} className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary-dark">
          Speichern
        </button>
      </div>
    </div>
  );
}

export default ArbeitsnachweisModal;
